import React from 'react'
import _ from 'lodash'
import {PARCEL_CONDITION_TYPES, PARCEL_CONDITION_VARIABLES} from 'server/functions/parcels/parcelConditions.js'
import type {ParcelConditionVariable} from 'server/functions/parcels/parcelConditions.js'
import type {Condition, ConditionValue} from 'server/utils/conditions.js'
import {Select, MetricInput} from '@components/UI/forms/index.js'
import t from 'src/helpers/Languages.js'
import Countries from 'src/utils/Countries.js'
import {Reseller} from 'types/types.js'
import ConditionGroupsEditor, {ConditionGroupsEditorProps, ConditionVariableOption} from './ConditionGroupsEditor.js'

const VARIABLE_TITLES: Record<ParcelConditionVariable, () => string> = {
    weight: () => t('weight'),
    length: () => t('length'),
    width: () => t('width'),
    height: () => t('height'),
    volume: () => t('volume'),
    options: () => t('parcelProducts'),
    country: () => t('country'),
    deliveryZone: () => t('deliveryZone'),
    postalCode: () => t('postalCode'),
    isBusiness: () => t('businessAddress')
}

const SIZES: {[variable in ParcelConditionVariable]?: {divisionFactor: number, append: string}} = {
    weight: {divisionFactor: 1000, append: 'kg'},
    length: {divisionFactor: 10, append: 'cm'},
    width: {divisionFactor: 10, append: 'cm'},
    height: {divisionFactor: 10, append: 'cm'},
    volume: {divisionFactor: 1000000, append: 'dm3'}
}

interface ParcelConditionGroupsEditorProps extends ConditionGroupsEditorProps<ParcelConditionVariable> {
    reseller: Reseller
}

/** Edits conditions on parcels: automatic parcel fees and shipping rules */
function ParcelConditionGroupsEditor({reseller, ...props}: ParcelConditionGroupsEditorProps) {
    const variableOptions: ConditionVariableOption<ParcelConditionVariable>[] = PARCEL_CONDITION_VARIABLES.map((variable) => ({value: variable, title: VARIABLE_TITLES[variable]()}))

    // The shipping options enabled for one of the carriers, and those already chosen
    const enabledOptions = Object.keys(reseller.settings.parcels.products || {}).filter((option) => {
        return _.some(reseller.settings.parcels.products[option], (enabled) => !!enabled)
    })
    const chosenOptions = props.conditionGroups.flat().filter((condition) => condition.variable === 'options').flatMap((condition) => Array.isArray(condition.value) ? condition.value : [])
    const optionOptions = _.uniq([...enabledOptions, ...chosenOptions]).sort().map((option) => ({value: option, title: option}))

    const renderValue = (condition: Condition<ParcelConditionVariable>, onChange: (value: ConditionValue) => void) => {
        const values = Array.isArray(condition.value) ? condition.value : []
        const size = SIZES[condition.variable]

        switch (condition.variable) {
            case 'options':
                return <Select values={values} options={optionOptions} onChange={(event) => onChange(event.target.values)} />
            case 'country':
                return <Select values={values} options={Countries.map((country) => ({value: country.code2, title: country.name}))} onChange={(event) => onChange(event.target.values)} />
            case 'deliveryZone':
                return <Select values={values} options={reseller.settings.deliveryZones.map((deliveryZone) => ({value: deliveryZone.id, title: deliveryZone.name}))} onChange={(event) => onChange(event.target.values)} />
            default:
                return size && (
                    <MetricInput
                        max={999999999}
                        info={['length', 'width', 'height'].includes(condition.variable) ? t('parcelSizeConditionInfo') : undefined}
                        divisionFactor={size.divisionFactor}
                        append={size.append}
                        value={condition.value as number}
                        onChange={(event) => onChange(parseInt(event.target.value.toString()) || 0)}
                    />
                )
        }
    }

    return (
        <ConditionGroupsEditor
            {...props}
            variables={variableOptions}
            types={PARCEL_CONDITION_TYPES}
            renderValue={renderValue}
        />
    )
}

export default ParcelConditionGroupsEditor
